import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {apiHttp} from '../endpoints';
import useAuth from './useAuth';
import {useProfileFieldsStatus} from './useProfileFieldsStatus';

/**
 * Touchpoint action types
 */
export type TouchpointAction = 'prompt' | 'access' | 'complete';

/**
 * Known touchpoint slugs from the backend
 */
export type TouchpointSlug =
  | 'onboarding_flow'
  | 'user_handle_update'
  | 'profile_picture_update'
  | 'home_address_update'
  | 'home_address_confirmation'
  | 'meet_the_connector_view'
  | 'bookmark_ahead_session';

/**
 * Touchpoint summary for a user (flat structure from API)
 */
export type TouchpointSummary = {
  touchpoint: TouchpointSlug;
  display_name: string;
  prompt_order: number | null;
  prompt_frequency_days: number | null;
  completed_count: number;
  first_completed_at: string | null;
  last_completed_at: string | null;
  last_prompted_at: string | null;
  last_accessed_at: string | null;
};

/**
 * Response from GET /api/user/touchpoint-summaries
 */
type TouchpointSummariesResponse = {
  data: TouchpointSummary[];
};

/**
 * Request body for POST /api/user/touchpoint-logs
 */
type LogTouchpointRequest = {
  touchpoint: TouchpointSlug;
  action: TouchpointAction;
  metadata?: Record<string, unknown>;
};

// Query key for touchpoint summaries
const TOUCHPOINT_SUMMARIES_KEY = 'touchpointSummaries';

/**
 * Fetch touchpoint summaries from the API
 */
const fetchTouchpointSummaries = async (): Promise<TouchpointSummary[]> => {
  const response = await apiHttp.get({
    url: '/user/touchpoint-summaries',
  });
  return (response.data as TouchpointSummariesResponse).data;
};

/**
 * Log a touchpoint interaction
 */
const logTouchpoint = async (params: LogTouchpointRequest): Promise<void> => {
  await apiHttp.post({
    url: '/user/touchpoint-logs',
    params,
  });
};

/**
 * Hook to fetch user's touchpoint summaries
 */
export const useTouchpointSummaries = () => {
  const {user} = useAuth();

  return useQuery([TOUCHPOINT_SUMMARIES_KEY], fetchTouchpointSummaries, {
    enabled: !!user,
    staleTime: 1000 * 60 * 5, // 5 minutes
    cacheTime: 1000 * 60 * 30, // 30 minutes
  });
};

/**
 * Hook to log a touchpoint interaction
 */
export const useLogTouchpoint = () => {
  const queryClient = useQueryClient();

  return useMutation(logTouchpoint, {
    onSuccess: () => {
      // Invalidate summaries to refetch updated state
      queryClient.invalidateQueries([TOUCHPOINT_SUMMARIES_KEY]);
    },
  });
};

/**
 * Helper hook to check if a specific touchpoint is complete
 */
export const useTouchpointComplete = (slug: TouchpointSlug): boolean => {
  const {data: summaries, isLoading} = useTouchpointSummaries();

  if (isLoading || !summaries) {
    return true; // Assume complete while loading to avoid flashing banners
  }

  const summary = summaries.find((s) => s.touchpoint === slug);
  return (summary?.completed_count ?? 0) > 0;
};

/**
 * Helper hook to check if the onboarding flow is complete
 */
export const useIsOnboardingComplete = (): {isComplete: boolean; isLoading: boolean} => {
  const {data: summaries, isLoading} = useTouchpointSummaries();

  if (isLoading || !summaries) {
    return {isComplete: true, isLoading: true}; // Assume complete while loading
  }

  const onboardingSummary = summaries.find((s) => s.touchpoint === 'onboarding_flow');
  const isComplete = (onboardingSummary?.completed_count ?? 0) > 0;

  return {isComplete, isLoading: false};
};

/**
 * Step-to-touchpoint mapping for onboarding resume functionality
 * Each step maps to a touchpoint that indicates completion
 */
const ONBOARDING_STEP_TOUCHPOINTS: Array<{step: number; slug: TouchpointSlug}> = [
  {step: 7, slug: 'home_address_update'},
  {step: 8, slug: 'user_handle_update'},
  {step: 9, slug: 'profile_picture_update'},
];

/**
 * Map touchpoint slugs to profile field keys for checking actual values.
 * A step is "complete" if the touchpoint was logged OR the field already has a value.
 */
const TOUCHPOINT_FIELD_MAP: Record<string, 'home_address' | 'handle' | 'profile_photo'> = {
  home_address_update: 'home_address',
  user_handle_update: 'handle',
  profile_picture_update: 'profile_photo',
};

/**
 * Hook to determine where to resume onboarding based on completed touchpoints
 * AND actual profile field values. A step is considered complete if either
 * the touchpoint was logged or the field already has a value.
 */
export const useOnboardingResumeStep = (): {
  firstIncompleteStep: number | null;
  firstIncompleteStepUrl: string | null;
  completedStepCount: number;
  isLoading: boolean;
} => {
  const {data: summaries, isLoading} = useTouchpointSummaries();
  const {isComplete: isOnboardingComplete} = useIsOnboardingComplete();
  const profileFields = useProfileFieldsStatus();

  if (isLoading || !summaries || profileFields.isLoading) {
    return {
      firstIncompleteStep: null,
      firstIncompleteStepUrl: null,
      completedStepCount: 0,
      isLoading: true,
    };
  }

  // If onboarding is complete, no resume needed
  if (isOnboardingComplete) {
    return {
      firstIncompleteStep: null,
      firstIncompleteStepUrl: null,
      completedStepCount: ONBOARDING_STEP_TOUCHPOINTS.length + 1, // All steps complete
      isLoading: false,
    };
  }

  // Check each step's touchpoint to find first incomplete
  let completedStepCount = 0;
  let firstIncompleteStep = 7; // Default to first onboarding step (step 7)

  for (const {step, slug} of ONBOARDING_STEP_TOUCHPOINTS) {
    const summary = summaries.find((s) => s.touchpoint === slug);
    const isTouchpointComplete = (summary?.completed_count ?? 0) > 0;

    // Also check if the field already has a value
    const fieldKey = TOUCHPOINT_FIELD_MAP[slug];
    const hasFieldValue = fieldKey ? profileFields[fieldKey]?.has_value : false;

    const isStepComplete = isTouchpointComplete || hasFieldValue;

    if (isStepComplete) {
      completedStepCount++;
      // Move to next step since this one is complete
      firstIncompleteStep = step + 1;
    } else {
      // Found the first incomplete step
      break;
    }
  }

  // Cap at step 10 (the final step)
  if (firstIncompleteStep > 10) {
    firstIncompleteStep = 10;
  }

  return {
    firstIncompleteStep,
    firstIncompleteStepUrl: `/onboarding?step=${firstIncompleteStep}`,
    completedStepCount,
    isLoading: false,
  };
};

/**
 * Get touchpoints that should be prompted to the user
 * Sorted by prompt_order (lower = higher priority)
 */
export const usePromptableTouchpoints = (): {
  touchpoints: TouchpointSummary[];
  isLoading: boolean;
} => {
  const {data: summaries, isLoading} = useTouchpointSummaries();

  if (isLoading || !summaries) {
    return {touchpoints: [], isLoading: true};
  }

  // Filter to promptable touchpoints that need action
  const promptable = summaries
    .filter((s) => {
      // Must have a prompt_order to be promptable
      if (s.prompt_order === null) return false;

      // Check if never completed
      if (s.completed_count === 0) return true;

      // Check if recurring and enough time has passed
      const freqDays = s.prompt_frequency_days;
      if (freqDays && s.last_completed_at) {
        const daysSinceComplete = Math.floor(
          (Date.now() - new Date(s.last_completed_at).getTime()) / (1000 * 60 * 60 * 24),
        );
        return daysSinceComplete >= freqDays;
      }

      return false;
    })
    .sort((a, b) => (a.prompt_order ?? 999) - (b.prompt_order ?? 999));

  return {touchpoints: promptable, isLoading: false};
};
